import { call, put, race } from 'redux-saga/effects';
import * as gereralActions from './actions';
import { APIScenarioReturn, TAPIReturnError, TAPIReturnSuccess } from './apiSagaFactory';

export type TSagaReturnSuccess<TReturn = any> = {
  success: true;
  args: any;
  data: TReturn;
};
export type TSagaReturnFailure = {
  success: false;
  args: any;
  reason: Interrupt;
};
export type TSagaReturn<TReturn = any> = TSagaReturnSuccess<TReturn> | TSagaReturnFailure;

export function isSuccessful<TReturn>(
  ret: TSagaReturn<TReturn>,
): ret is TSagaReturnSuccess<TReturn> {
  return ret.success;
}

export function isSuccessfulApiScenarioReturn<TReturn>(
  ret: APIScenarioReturn<TReturn>,
): ret is TAPIReturnSuccess<TReturn> {
  return ret.success;
}
export function allSuccessfulApiScenarioReturn<TReturn>(
  results: APIScenarioReturn<TReturn>[],
): results is TAPIReturnSuccess<TReturn>[] {
  return results.every(isSuccessfulApiScenarioReturn);
}
export function isFailed<TReturn>(result: TSagaReturn<TReturn>): result is TSagaReturnFailure {
  return !result.success;
}
export function isFailedApiScenarioReturn<TReturn>(
  result: APIScenarioReturn<TReturn>,
): result is TAPIReturnError {
  return !result.success;
}

/**
 * обычная сага. возвращает формальный ответ TSagaReturn
 * @deprecated
 */
export type TSaga<TReturn = any> = Generator<any, TSagaReturn<TReturn>, any>;

/**
 * упрощенный вид саги, для использования внутри сценариев. Либо возвращает TReturn, либо крашится (сценарий обработает краш)
 * @deprecated
 */
export type TSimpleSaga<TReturn = any> = Generator<any, TReturn, any>;

/**
 * сага, которая просто возвращает true, для управления сценариями
 * @deprecated
 */
export type TTrueSaga = Generator<any, true, any>;
export type TScenario<TArgs extends any[], TReturn = any> = (...args: TArgs) => TSaga<TReturn>;
type TSimpleScenario<TArgs extends any[]> = (...args: TArgs) => TSimpleSaga;
export enum InterruptType {
  USER = 'USER',
  EXCEPTION = 'EXCEPTION',
  API = 'API',
}
export enum InterruptReason {
  USER_CANCEL = 'USER_CANCEL',
  API = 'API',
}

export class Interrupt {
  type: InterruptType;
  message?: string;
  constructor(type: InterruptType, message?: string) {
    this.type = type;
    this.message = message;
  }
  static Exception(message?: string) {
    return new Interrupt(InterruptType.EXCEPTION, message);
  }
  static User(message?: string) {
    return new Interrupt(InterruptType.USER, message);
  }
  static readonly UserCancelled: Interrupt = Interrupt.User(InterruptReason.USER_CANCEL);
  static readonly API: Interrupt = Interrupt.Exception(InterruptReason.API);
}

/* eslint-disable no-console */
/* tslint:disable: no-console */
const group = (label: string) => {
  if (process.env.NODE_ENV === 'development') {
    console.group(label);
  }
};
const groupEnd = () => {
  if (process.env.NODE_ENV === 'development') {
    console.groupEnd();
  }
};
/**
 * @deprecated
 */
export function scenarioFactory<TArgs extends any[], TReturn = any>(
  scenario: TSimpleScenario<TArgs>,
  cancelSaga: ((...args: TArgs) => TTrueSaga) | null | undefined,
  cleanupSaga: ((...args: TArgs) => TTrueSaga) | null | undefined,
  key: string | undefined,
): TScenario<TArgs, TReturn> {
  return function* (...args) {
    const conclude = function* () {
      if (cleanupSaga) {
        yield call(cleanupSaga, ...args);
      }
      if (key) {
        yield put(gereralActions.scenarioEnd({ key }));
        groupEnd();
      }
    };
    if (key) {
      group(key);
      yield put(gereralActions.scenarioBegin({ key }));
    }
    try {
      const scenarioCall = call(scenario, ...args);
      const calls = cancelSaga ? [scenarioCall, call(cancelSaga, ...args)] : [scenarioCall];
      const [data, cancel] = yield race(calls);
      if (cancel) {
        throw Interrupt.Exception('request');
      }
      yield call(conclude);
      return { success: true, args, data } as TSagaReturn;
    } catch (reason) {
      yield call(conclude);
      return { success: false, args, reason } as TSagaReturn;
    }
  };
}
